import { useEffect, useId } from "react";

// The title of the browser tab: `<Page> · Lafayette Staff`. The shell sets one from the route (`useRouteDocumentTitle`). A page that
// knows a better one calls `useDocumentTitle("Refund")`; the page wins over the shell while it is mounted, and the shell title comes
// back when it unmounts. A title is fixed wording: never customer text, because tab titles are copied to history and to error reports.

export const APP_TITLE = "Lafayette Staff";

type Entry = Readonly<{ title: string; priority: number; order: number }>;
const entries = new Map<string, Entry>();
let counter = 0;

function apply(): void {
  let best: Entry | null = null;
  for (const entry of entries.values()) {
    if (
      best === null ||
      entry.priority > best.priority ||
      (entry.priority === best.priority && entry.order > best.order)
    ) {
      best = entry;
    }
  }
  document.title = best === null ? APP_TITLE : `${best.title} · ${APP_TITLE}`;
}

function useRegisteredTitle(title: string, priority: number): void {
  const id = useId();
  useEffect(() => {
    counter += 1;
    entries.set(id, { title, priority, order: counter });
    apply();
    return () => {
      entries.delete(id);
      apply();
    };
  }, [id, title, priority]);
}

/** For a page: sets the tab title while the page is mounted. */
export function useDocumentTitle(title: string): void {
  useRegisteredTitle(title, 1);
}

/** For the shell: the title of the route, which a page may override. */
export function useRouteDocumentTitle(title: string): void {
  useRegisteredTitle(title, 0);
}
